import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tabs,
  Tab,
  Collapse,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Schedule as ScheduleIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import { EmailAddress, IncomingEmail, LowConfidenceAlert } from '../../types/dashboard';
import { useDashboardContext } from '../../contexts/DashboardContext';
import CustomButton from '../ui/Button';
import AlertStatus from '../ui/AlertStatus';
import ExecutionTimeDisplay from '../ui/ExecutionTimeDisplay';
import PageContainer from '../layout/PageContainer';
import PageHeader, { Accent } from '../layout/PageHeader';

export interface EmailManagementProps {
  emailAddress: EmailAddress;
  incomingEmails: IncomingEmail[];
  lowConfidenceAlerts: LowConfidenceAlert[];
  onEmailCompromise: () => void;
  onRefreshEmails: () => void;
  onProcessLowConfidence: (alertId: string, action: 'process' | 'delete' | 'learn') => void;
  loading?: boolean;
  storageUsage: number; // in GB
  storageLimit: number; // in GB
}

const EmailManagement: React.FC<EmailManagementProps> = ({
  emailAddress,
  incomingEmails,
  lowConfidenceAlerts,
  onEmailCompromise,
  onRefreshEmails,
  onProcessLowConfidence,
  loading = false,
  storageUsage,
  storageLimit,
}) => {
  const { user, tenant } = useDashboardContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [showCompromiseDialog, setShowCompromiseDialog] = useState(false);
  const [, setSelectedAlert] = useState<LowConfidenceAlert | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Map the backend execution_status to a human label + color. Falls back to the
  // processed flag for older rows that don't carry an explicit status.
  const statusDisplay = (
    email: IncomingEmail,
  ): { label: string; status: 'success' | 'error' | 'warning' | 'info' | 'inactive' } => {
    switch (email.executionStatus) {
      case 'success':
        return { label: 'Executed', status: 'success' };
      case 'activated':
        return { label: 'Indicator activated', status: 'success' };
      case 'deactivated':
        return { label: 'Indicator reset', status: 'info' };
      case 'error':
        return { label: 'Failed', status: 'error' };
      case 'pending':
        return { label: 'Parsed — no matching alert', status: 'info' };
      case 'low_confidence':
        return { label: 'Low confidence', status: 'warning' };
      case 'unparsed':
        return { label: 'Not a recognized alert', status: 'inactive' };
      case 'received':
        return { label: 'Received', status: 'inactive' };
      default:
        return email.processed
          ? { label: 'Processed', status: 'success' }
          : { label: 'Pending', status: 'warning' };
    }
  };

  const filteredEmails = incomingEmails.filter(email =>
    email.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    email.from.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const storagePercentage = (storageUsage / storageLimit) * 100;

  const handleEmailCompromise = () => {
    setShowCompromiseDialog(true);
  };

  const confirmEmailCompromise = () => {
    onEmailCompromise();
    setShowCompromiseDialog(false);
  };

  const handleLowConfidenceAction = (alert: LowConfidenceAlert, action: 'process' | 'delete' | 'learn') => {
    onProcessLowConfidence(alert.id, action);
    setSelectedAlert(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <PageContainer>
      <PageHeader
        title={<>Your <Accent>email</Accent></>}
        subtitle={`${tenant?.name || 'Your Organization'} — ${user?.name || 'User'}`}
      />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Incoming Emails" />
          <Tab label="Low Confidence Alerts" />
          <Tab label="Management & Settings" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Card>
          <CardHeader
            title="Incoming Emails"
            subheader="Recent emails received and processed"
            action={
              <TextField
                size="small"
                placeholder="Search emails..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            }
          />
          <CardContent>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>From</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Received</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Execution Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEmails.map((email) => {
                    const status = statusDisplay(email);
                    const expanded = expandedId === email.id;
                    return (
                      <React.Fragment key={email.id}>
                        <TableRow
                          hover
                          sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
                          onClick={() => setExpandedId(expanded ? null : email.id)}
                        >
                          <TableCell padding="checkbox">
                            <IconButton size="small" aria-label="expand row">
                              {expanded ? <CollapseIcon /> : <ExpandIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{email.from}</TableCell>
                          <TableCell>{email.subject}</TableCell>
                          <TableCell>{formatDate(email.receivedAt)}</TableCell>
                          <TableCell>
                            <AlertStatus status={status.status} label={status.label} size="small" />
                          </TableCell>
                          <TableCell>
                            {email.executionTime && (
                              <ExecutionTimeDisplay
                                executionTime={email.executionTime}
                                status="success"
                                size="small"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                              <Box sx={{ my: 2, mx: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Message
                                </Typography>
                                {email.confidenceScore !== undefined && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Parser confidence: {(email.confidenceScore * 100).toFixed(0)}%
                                  </Typography>
                                )}
                                <Box
                                  component="pre"
                                  sx={{
                                    mt: 1,
                                    p: 1.5,
                                    borderRadius: 1,
                                    bgcolor: 'action.hover',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                    maxHeight: 320,
                                    overflow: 'auto',
                                  }}
                                >
                                  {email.body?.trim()
                                    ? email.body
                                    : '(No message body was captured for this email.)'}
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Card>
          <CardHeader
            title={
              <Box display="flex" alignItems="center" gap={1}>
                <WarningIcon color="warning" />
                Low Confidence Alerts
                <Chip label={lowConfidenceAlerts.length} color="warning" size="small" />
              </Box>
            }
            subheader="Alerts that need your attention"
          />
          <CardContent>
            {lowConfidenceAlerts.length === 0 ? (
              <Alert severity="success">
                No low confidence alerts at this time. All alerts are processing normally.
              </Alert>
            ) : (
              lowConfidenceAlerts.map((alert) => (
                <Box key={alert.id} sx={{ mb: 2, p: 2, border: 1, borderColor: 'warning.main', borderRadius: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                    <Typography variant="subtitle2">
                      {alert.parsedData?.stockSymbol || 'Unknown'} - {alert.parsedData?.positionType || 'Unknown'}
                    </Typography>
                    <AlertStatus
                      status="warning"
                      label={`${alert.confidenceScore}% confidence`}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {alert.reason}
                  </Typography>
                  <Box display="flex" gap={1}>
                    <CustomButton
                      variant="primary"
                      size="small"
                      onClick={() => handleLowConfidenceAction(alert, 'process')}
                    >
                      Process Now
                    </CustomButton>
                    <CustomButton
                      variant="secondary"
                      size="small"
                      onClick={() => handleLowConfidenceAction(alert, 'learn')}
                    >
                      Process Future
                    </CustomButton>
                    <CustomButton
                      variant="danger"
                      size="small"
                      onClick={() => handleLowConfidenceAction(alert, 'delete')}
                    >
                      Delete
                    </CustomButton>
                  </Box>
                </Box>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Box>
          {/* Generated Email Address */}
          <Card sx={{ mb: 3 }}>
            <CardHeader
              title="Generated Email Address"
              subheader="Use this email address in your signal source alerts"
              action={
                <Box display="flex" gap={1}>
                  <CustomButton
                    variant="secondary"
                    size="small"
                    onClick={onRefreshEmails}
                    startIcon={<RefreshIcon />}
                    disabled={loading}
                  >
                    Refresh
                  </CustomButton>
                </Box>
              }
            />
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    flex: 1,
                    color: 'text.primary',
                  }}
                >
                  {emailAddress.address}
                </Box>
                <Tooltip title="Copy email address">
                  <IconButton
                    onClick={() => navigator.clipboard.writeText(emailAddress.address)}
                    size="large"
                  >
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box display="flex" gap={2} alignItems="center">
                <Chip
                  icon={<EmailIcon />}
                  label={emailAddress.isCompromised ? 'Compromised' : 'Active'}
                  color={emailAddress.isCompromised ? 'error' : 'success'}
                  size="small"
                />
                <Chip
                  icon={<ScheduleIcon />}
                  label={`Created ${formatDate(emailAddress.createdAt)}`}
                  variant="outlined"
                  size="small"
                />
              </Box>

              {emailAddress.isCompromised && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This email address has been compromised. Please generate a new one and update your signal source alerts.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Storage Usage */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <StorageIcon />
                <Typography variant="h6">Storage Usage</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2">
                  {storageUsage.toFixed(2)} GB / {storageLimit} GB
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {storagePercentage.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={storagePercentage}
                color={storagePercentage > 80 ? 'error' : storagePercentage > 60 ? 'warning' : 'primary'}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                12-month retention policy • 1GB storage limit
              </Typography>
            </CardContent>
          </Card>

          {/* Email Compromise Button */}
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <SecurityIcon color="error" />
                <Box flex={1}>
                  <Typography variant="h6" color="error">
                    Email Compromised?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    If your email address has been compromised, generate a new one and update your signal source alerts.
                  </Typography>
                </Box>
                <CustomButton
                  variant="danger"
                  onClick={handleEmailCompromise}
                  disabled={loading}
                >
                  Generate New Email
                </CustomButton>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Email Compromise Confirmation Dialog */}
      <Dialog open={showCompromiseDialog} onClose={() => setShowCompromiseDialog(false)}>
        <DialogTitle>Generate New Email Address</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will deactivate your current email address. You will need to update all your signal source alerts with the new email address.
          </Alert>
          <Typography>
            Are you sure you want to generate a new email address? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={() => setShowCompromiseDialog(false)}>
            Cancel
          </CustomButton>
          <CustomButton variant="danger" onClick={confirmEmailCompromise}>
            Generate New Email
          </CustomButton>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default EmailManagement;
